import { linePrices } from '@/lib/quote-metrics.generated';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Variable, LookupTable } from '@/features/pricing-models/types/model.types';
import { Material } from '@/features/materials/types/material.types';
import { VariableType } from '@/types';
import { Plus, Trash2, Package, AlertCircle, Lock, Ship } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { UpstreamMaterialPrice } from '../types/quote.types';
import {
  calculateVariables,
  resolveTieredVariable,
} from '@/features/pricing-models/utils/formula-evaluator';
import { formatNumber } from '@/lib/utils/format';
import { MaterialCombobox } from './material-combobox';
import { PriceListPicker } from './price-list-picker';
import type { QuoteShipment } from '../types/quote-shipment.types';

export interface LineItemRow {
  id: string;
  materialId: string;
  quantity: number;
  costInputs: Record<string, any>;
  unitPrice: number;
  totalPrice: number;
  calculations: Record<string, any>;
  shipmentId?: string | null;
}

/**
 * Resuelve el precio unitario a mostrar en la tabla.
 * Prioridad:
 *   1. lineItem.unitPrice (precio calculado por el modelo, p.ej. variable con priceRole=UNIT_PRICE)
 *   2. costInputs.COSTO_UNITARIO (fallback: costo real cuando el modelo no calcula precio)
 *   3. material.unitPrice (fallback al default del material maestro)
 */
function resolveDisplayUnitPrice(item: LineItemRow, material?: Material | null): number {
  const up = Number(item?.unitPrice);
  if (Number.isFinite(up) && up > 0) return up;

  const fromCostInputs = item?.costInputs?.COSTO_UNITARIO;
  const ciNum = fromCostInputs != null && fromCostInputs !== '' ? Number(fromCostInputs) : NaN;
  if (Number.isFinite(ciNum) && ciNum > 0) return ciNum;

  const matPrice = Number((material as any)?.unitPrice ?? (material as any)?.price);
  if (Number.isFinite(matPrice) && matPrice > 0) return matPrice;

  return Number(item?.unitPrice) || 0;
}

interface LineItemsTableProps {
  allVariables: Variable[];
  perItemVariables: Variable[];
  globalVariables?: Variable[];
  /** Lookup tables del modelo, para que LOOKUP() en fórmulas las pueda resolver. */
  lookupTables?: LookupTable[];
  globalInputs: Record<string, any>;
  materials: Material[];
  lineItems: LineItemRow[];
  onChange: (lineItems: LineItemRow[]) => void;
  currencySymbol?: string;
  upstreamPrices?: Record<string, UpstreamMaterialPrice>;
  upstreamVariableCode?: string;
  freightByLineItemId?: Record<string, number>;
  /** Shipments disponibles del quote. Si se pasa (aunque sea []), se renderiza la columna "Envío". */
  shipments?: QuoteShipment[];
  /** Cambia el shipmentId de una línea (llamado al elegir otro envío en el dropdown). */
  onShipmentChange?: (lineItemId: string, shipmentId: string) => Promise<void> | void;
  /** Crea un nuevo shipment y devuelve el shipment creado (para autoasignar a la línea). */
  onCreateShipment?: () => Promise<QuoteShipment | null>;
  /** Asegura que exista al menos un shipment y devuelve su id (para la primera línea). */
  onEnsureDefaultShipment?: () => Promise<QuoteShipment | null>;
  /** IDs de materiales que deberían aparecer primero en el combobox (ej.: de cotización origen). */
  priorityMaterialIds?: string[];
}

let nextTempId = 1;

function generateTempId(): string {
  return `temp-${nextTempId++}`;
}

export function LineItemsTable({
  allVariables,
  perItemVariables,
  globalVariables = [],
  lookupTables = [],
  globalInputs,
  materials,
  lineItems,
  onChange,
  currencySymbol = '$',
  upstreamPrices,
  upstreamVariableCode,
  freightByLineItemId,
  shipments,
  onShipmentChange,
  onCreateShipment,
  onEnsureDefaultShipment,
  priorityMaterialIds,
}: LineItemsTableProps) {
  const showFreightColumn =
    !!freightByLineItemId && Object.values(freightByLineItemId).some((v) => v > 0);
  const showShipmentColumn = Array.isArray(shipments);

  // GA (gravamen arancelario) por línea: % editable (default del material) × (costo + flete)
  // Guardamos el % override en costInputs.GA_PCT para que se persista con la cotización.
  const gaByLineItemId: Record<string, { rate: number; amount: number }> = {};
  for (const item of lineItems) {
    const mat = materials.find((m) => m.id === item.materialId);
    const defaultRate = (mat as any)?.activeTariffRate ?? 0;
    // Si el usuario editó el GA%, usar ese; si no, el del material
    const rate =
      item.costInputs?.GA_PCT != null && item.costInputs.GA_PCT !== ''
        ? Number(item.costInputs.GA_PCT)
        : defaultRate;
    const freight = freightByLineItemId?.[item.id] ?? 0;
    gaByLineItemId[item.id] = {
      rate,
      amount: rate > 0 ? ((item.totalPrice + freight) * rate) / 100 : 0,
    };
  }
  const isImportQuote = showShipmentColumn || showFreightColumn;
  const showGaColumn =
    isImportQuote && (Object.values(gaByLineItemId).some((v) => v.rate > 0) || showFreightColumn);
  // Merge per-item variables (excl. CANTIDAD) + global input variables, sorted by order
  const globalColumns = globalVariables.filter(
    (v) => !v.formula && v.variableType !== VariableType.CALCULATED
  );
  const allColumns = [
    ...perItemVariables.filter((v) => v.code !== 'CANTIDAD'),
    ...globalColumns,
  ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const recalculateRow = (row: LineItemRow): LineItemRow => {
    const combinedInputs = { ...globalInputs, ...row.costInputs, CANTIDAD: row.quantity };
    const results = calculateVariables(allVariables, combinedInputs, lookupTables);

    const calculations: Record<string, any> = {};
    results.forEach((r) => {
      calculations[r.variableCode] = r.value;
      if (r.error) {
        calculations[`${r.variableCode}_error`] = r.error;
      }
    });

    const { unitPrice, totalPrice } = linePrices(allVariables, calculations, row.quantity);

    return {
      ...row,
      unitPrice,
      totalPrice,
      calculations,
    };
  };

  // Recalcular filas que fueron agregadas externamente (cargar desde cotización origen,
  // import tracking, etc.) y aún no tienen `calculations` populadas.
  useEffect(() => {
    if (allVariables.length === 0) return;
    const needsRecalc = lineItems.some(
      (li) =>
        li.materialId &&
        Object.keys(li.calculations || {}).length === 0 &&
        Object.keys(li.costInputs || {}).length > 0
    );
    if (!needsRecalc) return;
    const recalculated = lineItems.map((li) => {
      if (
        li.materialId &&
        Object.keys(li.calculations || {}).length === 0 &&
        Object.keys(li.costInputs || {}).length > 0
      ) {
        return recalculateRow(li);
      }
      return li;
    });
    onChange(recalculated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems.length, allVariables.length]);

  // Recalcular items existentes cuando cambian las Variables Globales (TIPO_CAMBIO,
  // FACTOR_USA, OBSOLESCENCIA, etc.) — sin esto, el item se queda con los valores
  // calculados al momento de agregarse.
  const globalInputsKey = JSON.stringify(globalInputs);
  useEffect(() => {
    if (allVariables.length === 0) return;
    if (lineItems.length === 0) return;
    const recalculated = lineItems.map((li) => (li.materialId ? recalculateRow(li) : li));
    const changed = recalculated.some((li, i) => {
      const prev = lineItems[i];
      return (
        li.unitPrice !== prev.unitPrice ||
        li.totalPrice !== prev.totalPrice ||
        JSON.stringify(li.calculations) !== JSON.stringify(prev.calculations)
      );
    });
    if (changed) onChange(recalculated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalInputsKey, allVariables.length]);

  const handleAddItem = () => {
    // Pre-llenar costInputs con defaultValue de cada variable PER_LINE editable
    // (no calculadas, sin fórmula). Sin esto, el usuario ve campos vacíos incluso
    // cuando el modelo tiene defaults configurados.
    const defaultCostInputs: Record<string, any> = {};
    for (const v of perItemVariables) {
      if (v.variableType === VariableType.CALCULATED) continue;
      if (v.formula) continue;
      if (v.code === 'CANTIDAD') continue;
      const dv = (v as any).defaultValue;
      if (dv != null && dv !== '') {
        defaultCostInputs[v.code] = dv;
      }
    }
    const newItem: LineItemRow = {
      id: generateTempId(),
      materialId: '',
      quantity: 1,
      costInputs: defaultCostInputs,
      unitPrice: 0,
      totalPrice: 0,
      calculations: {},
    };
    onChange([...lineItems, newItem]);
  };

  const handleRemoveItem = (id: string) => {
    onChange(lineItems.filter((item) => item.id !== id));
  };

  // Detect the cost input variable where the material's price list price should go
  // Priority: 1. priceRole=COST_INPUT, 2. known code names, 3. first currency input
  const costVariableCodes = [
    'COSTO_UNITARIO',
    'COSTO',
    'COSTO_UNITARIO_IMPORT',
    'COSTO_PRODUCTO',
    'PRECIO_BASE',
    'COST',
    'UNIT_COST',
    'BASE_COST',
    'VALOR_FOB',
    'FOB',
    'PRECIO_PROVEEDOR',
    'COSTO_PROVEEDOR',
  ];
  const costVariable =
    perItemVariables.find((v) => (v as any).priceRole === 'UNIT_COST') ??
    perItemVariables.find((v) => (v as any).priceRole === 'COST_INPUT') ??
    perItemVariables.find(
      (v) =>
        !v.formula &&
        v.variableType !== VariableType.CALCULATED &&
        v.code !== 'CANTIDAD' &&
        costVariableCodes.includes(v.code!)
    ) ??
    perItemVariables.find(
      (v) =>
        !v.formula &&
        !v.isGlobal &&
        v.variableType === VariableType.CURRENCY &&
        v.code !== 'CANTIDAD'
    );

  // Variables de logística presentes en el modelo (para auto-fill desde el material)
  const hasPesoVar = allVariables.some((v) => v.code === 'PESO_KG');
  const hasVolumenVar = allVariables.some((v) => v.code === 'VOLUMEN_CBM');
  const hasFobVar = allVariables.some((v) => v.code === 'VALOR_FOB');
  /*
    Puente con Comercia (reunión «Mini Prixia», 27-oct): el producto se elige con su
    costo nacionalizado y sólo se le pone margen. Sólo en un modelo que parte de un
    costo: uno de importación (con VALOR_FOB, o con el FOB como costo) calcula su
    propio flete y aranceles, y los cobraría dos veces.
  */
  const conCostoNacionalizado =
    !!costVariable && !hasFobVar && !['VALOR_FOB', 'FOB'].includes(costVariable.code ?? '');

  const handleMaterialChange = (id: string, materialId: string, priceOverride?: number) => {
    const nextItems = lineItems.map((item) => {
      if (item.id !== id) return item;

      // Auto-fill cost from material's default price list (o el override que el usuario eligió)
      const costInputs: Record<string, any> = { ...item.costInputs };
      const mat = materialId ? materials.find((m) => m.id === materialId) : null;

      if (costVariable && mat) {
        // Sin precio de lista, el costo nacionalizado de Comercia (si el modelo lo admite).
        const fallback =
          mat.priceListEntries?.find((e) => e.priceList?.isDefault)?.price ??
          (conCostoNacionalizado ? (mat.lastLandedCost ?? undefined) : undefined);
        const priceToUse = typeof priceOverride === 'number' ? priceOverride : fallback;
        if (typeof priceToUse === 'number') {
          costInputs[costVariable.code!] = priceToUse;
        }
      }

      // Auto-fill logística desde los datos master del material (no sobrescribe valores ya tipeados)
      if (mat) {
        // MARCA_PRODUCTO: marca del material (usado por LOOKUP CEBE_NAC_GAR en modelos EXTERIOR)
        const hasMarcaVar = allVariables.some((v) => v.code === 'MARCA_PRODUCTO');
        if (hasMarcaVar) {
          const brand = String((mat as any).brand || mat.name || '')
            .trim()
            .toUpperCase();
          if (brand) costInputs.MARCA_PRODUCTO = brand;
        }

        // PESO_KG: material.weight (ya en kg)
        if (
          hasPesoVar &&
          (costInputs.PESO_KG == null || costInputs.PESO_KG === '' || costInputs.PESO_KG === 0) &&
          mat.weight != null
        ) {
          costInputs.PESO_KG = mat.weight;
        }

        // VOLUMEN_CBM: width × height × depth (cm³) → m³ dividiendo por 1_000_000
        if (
          hasVolumenVar &&
          (costInputs.VOLUMEN_CBM == null ||
            costInputs.VOLUMEN_CBM === '' ||
            costInputs.VOLUMEN_CBM === 0) &&
          mat.width != null &&
          mat.height != null &&
          mat.depth != null
        ) {
          const cbm = (mat.width * mat.height * mat.depth) / 1_000_000;
          if (cbm > 0) {
            // redondear a 6 decimales para no guardar números absurdamente largos
            costInputs.VOLUMEN_CBM = Math.round(cbm * 1_000_000) / 1_000_000;
          }
        }

        // VALOR_FOB: precio elegido (override) o lista default del material
        if (
          hasFobVar &&
          (costInputs.VALOR_FOB == null ||
            costInputs.VALOR_FOB === '' ||
            costInputs.VALOR_FOB === 0)
        ) {
          const fallback = mat.priceListEntries?.find((e) => e.priceList?.isDefault)?.price;
          const priceToUse = typeof priceOverride === 'number' ? priceOverride : fallback;
          if (typeof priceToUse === 'number') {
            costInputs.VALOR_FOB = priceToUse;
          }
        }

        // GA_PCT: gravamen arancelario del material
        if (costInputs.GA_PCT == null || costInputs.GA_PCT === '' || costInputs.GA_PCT === 0) {
          const tariffRate = (mat as any).activeTariffRate;
          if (tariffRate != null && tariffRate > 0) {
            costInputs.GA_PCT = tariffRate;
          }
        }
      }

      const updated = { ...item, materialId, costInputs };
      return recalculateRow(updated);
    });

    onChange(nextItems);

    // Auto-asignar shipment: si la primera línea no tiene shipment, crear/usar "Envío principal".
    if (materialId && showShipmentColumn && onEnsureDefaultShipment) {
      const thisItem = nextItems.find((it) => it.id === id);
      if (thisItem && !thisItem.shipmentId) {
        void (async () => {
          try {
            const ship = await onEnsureDefaultShipment();
            if (ship && onShipmentChange) {
              await onShipmentChange(id, ship.id);
            }
          } catch {
            // non-fatal: si falla, el usuario puede asignarlo manualmente
          }
        })();
      }
    }
  };

  const handleQuantityChange = (id: string, raw: string) => {
    const parsed = parseInt(raw);
    if (isNaN(parsed) || parsed < 1) return;
    onChange(
      lineItems.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, quantity: parsed };
        return recalculateRow(updated);
      })
    );
  };

  const handleCostInputChange = (id: string, code: string, raw: string) => {
    const parsed = parseFloat(raw);
    if (isNaN(parsed) || parsed < 0) return;
    return handleInputChange(id, code, parsed);
  };

  const handleInputChange = (id: string, code: string, value: any) => {
    onChange(
      lineItems.map((item) => {
        if (item.id !== id) return item;
        const updated = {
          ...item,
          costInputs: { ...item.costInputs, [code]: value },
        };
        return recalculateRow(updated);
      })
    );
  };

  const grandTotal = lineItems.reduce((sum, item) => sum + item.totalPrice, 0);

  const unitPriceRoleVar = allVariables.find((v) => v.priceRole === 'UNIT_PRICE');
  const totalPriceRoleVar = allVariables.find((v) => v.priceRole === 'TOTAL_PRICE');

  const activeMaterials = materials.filter((m) => m.isActive);

  // Helper to get tier tooltip
  const getTierTooltip = (v: Variable, quantity: number): string => {
    if (!v.tiers || v.tiers.length === 0) return '';
    const { tier } = resolveTieredVariable(v.tiers, quantity);
    if (!tier) return 'Sin nivel aplicable';
    const maxStr = tier.maxQuantity != null ? tier.maxQuantity : '∞';
    return `Nivel: ${tier.minQuantity}-${maxStr} uds → $${tier.unitPrice ?? 0}/ud`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Items de Cotización
            </CardTitle>
            <CardDescription className="flex items-center gap-4 mt-1">
              <span>Agrega materiales y completa los costos por item</span>
              <span className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded bg-info-suave border border-info-borde" />
                  Llenar
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded bg-muted border border-border" />
                  Global (sección)
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded bg-ok-suave border border-ok-borde" />
                  Calculado
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded bg-purple-100 border border-purple-300" />
                  Escalonado
                </span>
              </span>
            </CardDescription>
          </div>
          <Button type="button" onClick={handleAddItem} size="sm">
            <Plus className="w-4 h-4 mr-1" />
            Agregar Item
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {lineItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No hay items en la cotización.</p>
            <p className="text-sm">Haz clic en "Agregar Item" para comenzar.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 font-medium w-8 bg-muted/50">#</th>
                  <th className="text-left p-2 font-medium min-w-[180px] bg-muted/50">Material</th>
                  <th className="text-right p-2 font-medium w-20 bg-info-suave text-info">Cant.</th>
                  {allColumns.map((v) => {
                    const isGlobal = !!v.isGlobal;
                    const isFormula = !!v.formula || v.variableType === VariableType.CALCULATED;
                    const isTiered = v.variableType === VariableType.TIERED;
                    const isEnum = v.variableType === VariableType.ENUM;
                    const isLookup = v.variableType === VariableType.LOOKUP;
                    return (
                      <th
                        key={v.code}
                        className={`text-right p-2 font-medium min-w-[110px] ${
                          isGlobal
                            ? 'bg-muted text-muted-foreground'
                            : isTiered
                              ? 'bg-purple-50 text-purple-800'
                              : isFormula
                                ? 'bg-ok-suave text-ok'
                                : isEnum || isLookup
                                  ? 'bg-info-suave text-info'
                                  : 'bg-info-suave text-info'
                        }`}
                        title={
                          isGlobal
                            ? `${v.code} (global compartido por la sección)`
                            : isFormula
                              ? `${v.code} = ${v.formula}`
                              : isTiered
                                ? `${v.code} (escalonado)`
                                : v.code
                        }
                      >
                        {v.name}
                      </th>
                    );
                  })}
                  <th
                    className="text-right p-2 font-medium w-28 bg-alerta-suave text-foreground border-l-2 border-alerta-borde"
                    title={
                      unitPriceRoleVar ? `Variable: ${unitPriceRoleVar.code}` : 'Precio unitario'
                    }
                  >
                    {unitPriceRoleVar ? unitPriceRoleVar.name : 'Precio Unit.'}
                  </th>
                  <th
                    className="text-right p-2 font-medium w-28 bg-alerta-suave text-foreground"
                    title={
                      totalPriceRoleVar ? `Variable: ${totalPriceRoleVar.code}` : 'Total línea'
                    }
                  >
                    {totalPriceRoleVar ? totalPriceRoleVar.name : 'Total Línea'}
                  </th>
                  {showFreightColumn && (
                    <th
                      className="text-right p-2 font-medium w-28 bg-info-suave text-foreground border-l-2 border-info-borde"
                      title="Flete prorrateado para esta línea"
                    >
                      Flete
                    </th>
                  )}
                  {showGaColumn && (
                    <th
                      className="text-right p-2 font-medium w-28 bg-alerta-suave text-foreground border-l-2 border-alerta-borde"
                      title="Gravamen Arancelario (% del material × costo total)"
                    >
                      GA
                    </th>
                  )}
                  {(showFreightColumn || showGaColumn) && (
                    <th
                      className="text-right p-2 font-medium w-28 bg-ok-suave text-foreground border-l-2 border-ok-borde"
                      title="Costo Almacén Unitario = (Total Línea + Flete + GA) / Cantidad"
                    >
                      C.Alm. Unit.
                    </th>
                  )}
                  {(showFreightColumn || showGaColumn) && (
                    <th
                      className="text-right p-2 font-medium w-32 bg-ok-suave text-foreground"
                      title="Costo Almacén Total = Total Línea + Flete + GA"
                    >
                      Costo Almacén
                    </th>
                  )}
                  {showShipmentColumn && (
                    <th
                      className="text-left p-2 font-medium min-w-[140px] bg-info-suave text-foreground border-l-2 border-info-borde"
                      title="Envío en el que viaja esta línea"
                    >
                      <span className="inline-flex items-center gap-1">
                        <Ship className="w-3.5 h-3.5" />
                        Envío
                      </span>
                    </th>
                  )}
                  <th className="p-2 w-10 bg-muted/50" />
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, index) => (
                  <tr key={item.id} className="border-b hover:bg-muted/20">
                    <td className="p-2 text-muted-foreground">{index + 1}</td>
                    <td className="p-2">
                      <MaterialCombobox
                        materials={activeMaterials}
                        value={item.materialId}
                        onSelect={(val, priceOverride) =>
                          handleMaterialChange(item.id, val, priceOverride)
                        }
                        conCostoNacionalizado={conCostoNacionalizado}
                        priorityMaterialIds={priorityMaterialIds}
                      />
                    </td>
                    <td className="p-2 bg-info-suave">
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={item.quantity}
                        onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                        className="h-8 text-xs text-right w-20"
                      />
                    </td>
                    {allColumns.map((v) => {
                      const isGlobal = !!v.isGlobal;
                      const isFormula = !!v.formula || v.variableType === VariableType.CALCULATED;
                      const isTiered = v.variableType === VariableType.TIERED;
                      const isEnum = v.variableType === VariableType.ENUM;
                      const isLookup = v.variableType === VariableType.LOOKUP;
                      const calcError = item.calculations[`${v.code}_error`];

                      // GLOBAL: read-only cell showing shared section-level value
                      if (isGlobal) {
                        const val = globalInputs[v.code!];
                        const numVal = val != null ? Number(val) : null;
                        return (
                          <td
                            key={v.code}
                            className="p-2 text-right font-mono text-xs bg-muted text-muted-foreground"
                            title={`${v.name}: valor global de la sección`}
                          >
                            {numVal != null ? formatNumber(numVal) : '-'}
                          </td>
                        );
                      }

                      // ENUM: render dropdown
                      if (isEnum && v.options) {
                        return (
                          <td key={v.code} className="p-2 bg-info-suave">
                            <SearchableSelect
                              options={v.options.map((opt) => ({
                                value: String(opt.value),
                                label: opt.label,
                              }))}
                              value={
                                item.costInputs[v.code!] != null
                                  ? String(item.costInputs[v.code!])
                                  : undefined
                              }
                              onValueChange={(val) =>
                                handleInputChange(item.id, v.code!, parseFloat(val))
                              }
                              placeholder="Seleccionar"
                              searchPlaceholder="Buscar..."
                              className="h-8 text-xs"
                            />
                          </td>
                        );
                      }

                      // TIERED: read-only with tooltip
                      if (isTiered) {
                        const val = item.calculations[v.code!];
                        const numVal = Number(val) || 0;
                        const tooltip = getTierTooltip(v, item.quantity);
                        return (
                          <td
                            key={v.code}
                            className="p-2 text-right font-mono text-xs bg-purple-50/40 text-purple-900 cursor-help"
                            title={tooltip}
                          >
                            {numVal !== 0 ? formatNumber(numVal) : '-'}
                          </td>
                        );
                      }

                      // LOOKUP: render row/col selectors or read-only value
                      if (isLookup && v.lookupTable?.data) {
                        const val = item.calculations[v.code!];
                        const numVal = val != null ? Number(val) : null;
                        return (
                          <td key={v.code} className="p-2 bg-info-suave">
                            <div className="flex flex-col gap-1">
                              <SearchableSelect
                                options={v.lookupTable.data.rows.map((r) => ({
                                  value: r,
                                  label: r,
                                }))}
                                value={item.costInputs[`${v.code}_ROW`] || undefined}
                                onValueChange={(val) =>
                                  handleInputChange(item.id, `${v.code}_ROW`, val)
                                }
                                placeholder={v.lookupTable.rowDimension}
                                searchPlaceholder="Buscar..."
                                className="h-6 text-xs"
                              />
                              <SearchableSelect
                                options={v.lookupTable.data.cols.map((c) => ({
                                  value: c,
                                  label: c,
                                }))}
                                value={item.costInputs[`${v.code}_COL`] || undefined}
                                onValueChange={(val) =>
                                  handleInputChange(item.id, `${v.code}_COL`, val)
                                }
                                placeholder={v.lookupTable.colDimension}
                                searchPlaceholder="Buscar..."
                                className="h-6 text-xs"
                              />
                              {numVal != null && (
                                <span className="text-right font-mono text-xs text-info">
                                  {formatNumber(numVal)}
                                </span>
                              )}
                            </div>
                          </td>
                        );
                      }

                      // Formula / Calculated: read-only
                      if (isFormula) {
                        const val = item.calculations[v.code!];
                        const numVal = Number(val) || 0;
                        return (
                          <td
                            key={v.code}
                            className="p-2 text-right font-mono text-xs bg-ok-suave text-foreground"
                            title={calcError ? `Error: ${calcError}` : `${v.code} = ${v.formula}`}
                          >
                            {calcError ? (
                              <span className="flex items-center justify-end gap-1 text-destructive">
                                <AlertCircle className="w-3 h-3" />
                                <span className="text-xs">Error</span>
                              </span>
                            ) : numVal !== 0 ? (
                              formatNumber(numVal)
                            ) : (
                              '-'
                            )}
                          </td>
                        );
                      }

                      // Check if this variable is locked by upstream price
                      const isUpstreamVar = upstreamVariableCode === v.code;
                      const upstreamPrice =
                        isUpstreamVar && item.materialId
                          ? upstreamPrices?.[item.materialId]
                          : undefined;

                      if (isUpstreamVar && upstreamPrice?.available) {
                        return (
                          <td key={v.code} className="p-2 bg-ok-suave">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center justify-end gap-1">
                                  <Lock className="w-3 h-3 text-ok" />
                                  <span className="font-mono text-xs text-ok font-semibold">
                                    {formatNumber(upstreamPrice.unitPrice)}
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  Precio de {upstreamPrice.sourceQuoteCode}: {currencySymbol}
                                  {formatNumber(upstreamPrice.unitPrice)}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </td>
                        );
                      }

                      if (
                        isUpstreamVar &&
                        item.materialId &&
                        upstreamPrices &&
                        !upstreamPrice?.available
                      ) {
                        return (
                          <td key={v.code} className="p-2 bg-alerta-suave">
                            <div className="flex items-center justify-end gap-1">
                              <AlertCircle className="w-3 h-3 text-alerta" />
                              <Input
                                type="number"
                                step="0.01"
                                value={item.costInputs[v.code!] ?? ''}
                                onChange={(e) =>
                                  handleCostInputChange(item.id, v.code!, e.target.value)
                                }
                                className="h-8 text-xs text-right border-alerta-borde"
                                placeholder="Sin precio"
                                title="Sin precio upstream aprobado"
                              />
                            </div>
                          </td>
                        );
                      }

                      // Input: editable field
                      const isCostVar = costVariable && v.code === costVariable.code;
                      const mat =
                        isCostVar && item.materialId
                          ? materials.find((m) => m.id === item.materialId)
                          : null;
                      const priceEntries = mat?.priceListEntries ?? [];

                      return (
                        <td key={v.code} className="p-2 bg-info-suave">
                          <div className="flex items-center gap-1">
                            <Input
                              type={
                                v.variableType === VariableType.NUMBER ||
                                v.variableType === VariableType.CURRENCY ||
                                v.variableType === VariableType.PERCENTAGE
                                  ? 'number'
                                  : 'text'
                              }
                              step="0.01"
                              value={item.costInputs[v.code!] ?? ''}
                              onChange={(e) =>
                                handleCostInputChange(item.id, v.code!, e.target.value)
                              }
                              className="h-8 text-xs text-right"
                              placeholder="0"
                            />
                            {isCostVar && priceEntries.length > 0 && (
                              <PriceListPicker
                                entries={priceEntries}
                                currentPrice={item.costInputs[v.code!]}
                                onSelectPrice={(price) => {
                                  handleCostInputChange(item.id, v.code!, String(price));
                                }}
                                currencySymbol={currencySymbol}
                              />
                            )}
                          </div>
                        </td>
                      );
                    })}
                    {(() => {
                      const matForRow = item.materialId
                        ? materials.find((m) => m.id === item.materialId)
                        : null;
                      const displayUnit = resolveDisplayUnitPrice(item, matForRow);
                      const displayTotal =
                        Number.isFinite(Number(item.totalPrice)) && Number(item.totalPrice) > 0
                          ? Number(item.totalPrice)
                          : displayUnit * (Number(item.quantity) || 0);
                      return (
                        <>
                          <td className="p-2 text-right font-mono text-xs font-semibold bg-alerta-suave border-l-2 border-alerta-borde">
                            {currencySymbol} {formatNumber(displayUnit)}
                          </td>
                          <td className="p-2 text-right font-mono text-xs font-bold bg-alerta-suave">
                            {currencySymbol} {formatNumber(displayTotal)}
                          </td>
                        </>
                      );
                    })()}
                    {showFreightColumn && (
                      <td className="p-2 text-right font-mono text-xs font-semibold bg-info-suave border-l-2 border-info-borde text-foreground">
                        {currencySymbol} {formatNumber(freightByLineItemId?.[item.id] ?? 0)}
                      </td>
                    )}
                    {showGaColumn && (
                      <td className="p-2 bg-alerta-suave border-l-2 border-alerta-borde">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            className="h-7 w-14 text-xs text-right"
                            value={item.costInputs?.GA_PCT ?? gaByLineItemId[item.id]?.rate ?? ''}
                            placeholder={String(gaByLineItemId[item.id]?.rate ?? 0)}
                            onChange={(e) =>
                              handleCostInputChange(item.id, 'GA_PCT', e.target.value)
                            }
                          />
                          <span className="text-[10px] text-alerta">%</span>
                        </div>
                        <div className="text-right font-mono text-[10px] text-foreground mt-0.5">
                          {currencySymbol} {formatNumber(gaByLineItemId[item.id]?.amount ?? 0)}
                        </div>
                      </td>
                    )}
                    {(showFreightColumn || showGaColumn) &&
                      (() => {
                        const costoAlmacen =
                          item.totalPrice +
                          (freightByLineItemId?.[item.id] ?? 0) +
                          (gaByLineItemId[item.id]?.amount ?? 0);
                        const costoAlmacenUnit =
                          item.quantity > 0 ? costoAlmacen / item.quantity : 0;
                        return (
                          <>
                            <td className="p-2 text-right font-mono text-xs font-semibold bg-ok-suave border-l-2 border-ok-borde text-foreground">
                              {currencySymbol} {formatNumber(costoAlmacenUnit)}
                            </td>
                            <td className="p-2 text-right font-mono text-xs font-extrabold bg-ok-suave text-foreground">
                              {currencySymbol} {formatNumber(costoAlmacen)}
                            </td>
                          </>
                        );
                      })()}
                    {showShipmentColumn && (
                      <td className="p-2 bg-info-suave border-l-2 border-info-borde">
                        <Select
                          value={item.shipmentId || '__NONE__'}
                          onValueChange={async (val) => {
                            if (val === '__NONE__') {
                              if (onShipmentChange) {
                                await onShipmentChange(item.id, '');
                              }
                              return;
                            }
                            if (val === '__NEW__') {
                              if (!onCreateShipment) return;
                              try {
                                const newShip = await onCreateShipment();
                                if (newShip && onShipmentChange) {
                                  await onShipmentChange(item.id, newShip.id);
                                }
                              } catch {
                                // non-fatal
                              }
                              return;
                            }
                            if (onShipmentChange) {
                              await onShipmentChange(item.id, val);
                            }
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Sin envío" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__NONE__" className="text-xs text-muted-foreground">
                              Sin envío
                            </SelectItem>
                            {(shipments ?? []).map((s) => (
                              <SelectItem key={s.id} value={s.id} className="text-xs">
                                {s.name}
                              </SelectItem>
                            ))}
                            {onCreateShipment && (
                              <SelectItem value="__NEW__" className="text-xs font-medium text-info">
                                + Nuevo envío
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </td>
                    )}
                    <td className="p-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleRemoveItem(item.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 bg-muted/50 font-bold">
                  <td colSpan={3 + allColumns.length + 1} className="p-2 text-right">
                    GRAN TOTAL:
                  </td>
                  <td className="p-2 text-right font-mono text-base bg-alerta-suave">
                    {currencySymbol} {formatNumber(grandTotal)}
                  </td>
                  {showFreightColumn && (
                    <td className="p-2 text-right font-mono text-base bg-info-suave text-foreground">
                      {currencySymbol}{' '}
                      {formatNumber(
                        lineItems.reduce((sum, li) => sum + (freightByLineItemId?.[li.id] ?? 0), 0)
                      )}
                    </td>
                  )}
                  {showGaColumn && (
                    <td className="p-2 text-right font-mono text-base bg-alerta-suave text-foreground">
                      {currencySymbol}{' '}
                      {formatNumber(
                        lineItems.reduce((sum, li) => sum + (gaByLineItemId[li.id]?.amount ?? 0), 0)
                      )}
                    </td>
                  )}
                  {(showFreightColumn || showGaColumn) &&
                    (() => {
                      const totalAlmacen = lineItems.reduce(
                        (sum, li) =>
                          sum +
                          li.totalPrice +
                          (freightByLineItemId?.[li.id] ?? 0) +
                          (gaByLineItemId[li.id]?.amount ?? 0),
                        0
                      );
                      const totalQty = lineItems.reduce((sum, li) => sum + li.quantity, 0);
                      return (
                        <>
                          <td className="p-2 text-right font-mono text-base bg-ok-suave text-foreground">
                            {currencySymbol}{' '}
                            {formatNumber(totalQty > 0 ? totalAlmacen / totalQty : 0)}
                          </td>
                          <td className="p-2 text-right font-mono text-base font-extrabold bg-ok-suave text-foreground">
                            {currencySymbol} {formatNumber(totalAlmacen)}
                          </td>
                        </>
                      );
                    })()}
                  {showShipmentColumn && <td />}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default LineItemsTable;
